import { useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import { trackSearchLoadedMore, type SearchFilters } from '@/lib/track'
import { useSearchResultsTracking } from '../hooks/useSearchResultsTracking'
import { type LocationSearchResult } from '@/api/accommodation/types'
import SearchResultCard from './searchResultCard'
import Error from '@/components/error'
import { type UseInfiniteQueryResult, type InfiniteData } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import CalendarEmptyIcon from '@/components/icons/calendarEmpty'
import { Link } from '@tanstack/react-router'
import { ArrowRightIcon } from 'lucide-react'
import { hasActiveFilters } from '../utils/searchSummary'
import { SIMILAR_MAX_RESULTS, SearchSimilar } from './SearchSimilar'

export interface SearchProps {
  queryResult: UseInfiniteQueryResult<InfiniteData<LocationSearchResult[], number>, Error>
  pageSize: number
  linkToSearch?: boolean
  linkButtonText?: string
  maxItems?: number
  onClearFilters?: () => void
  filters?: SearchFilters
  /** Offers places close to the search under few results. */
  showSimilar?: boolean
}

const SearchResultSkeleton = () => <Skeleton height="25rem" borderRadius="0.75rem" />

interface EmptyStateProps {
  onClearFilters?: () => void
  filtersActive: boolean
}

export const EmptyState = ({ onClearFilters, filtersActive }: EmptyStateProps) => (
  <div className="col-span-full flex flex-col items-center justify-center py-16 text-center">
    <CalendarEmptyIcon className="mb-4 h-12 w-12 text-slate-300" />
    <h3 className="mb-2 text-lg font-semibold text-slate-700">
      {filtersActive ? 'Žádné nabídky pro zvolené filtry' : 'Žádné volné nabídky'}
    </h3>
    <p className="mb-4 max-w-sm text-slate-500">
      {filtersActive ? 'Po zrušení filtrů uvidíte další nabídky.' : 'Momentálně nejsou k dispozici žádné nabídky.'}
    </p>
    {filtersActive && onClearFilters && (
      <Button variant="secondary" size="sm" onClick={onClearFilters}>
        Vymazat filtry
      </Button>
    )}
  </div>
)

const Search = ({
  queryResult,
  pageSize,
  linkToSearch,
  maxItems,
  onClearFilters,
  filters,
  showSimilar,
}: SearchProps) => {
  const { data, isFetching, isFetchingNextPage, error, hasNextPage, fetchNextPage } = queryResult

  const fullResults = data?.pages.flat() ?? []
  const allResults = maxItems ? fullResults.slice(0, maxItems) : fullResults
  const filtersActive = filters ? hasActiveFilters(filters) : false
  const showClearFiltersCta = !linkToSearch && !isFetching && !!onClearFilters && filtersActive && allResults.length > 0
  const canAutoLoad = !linkToSearch && !maxItems && hasNextPage
  // Only a search with something to loosen can have similar places.
  const similarVisible =
    !!showSimilar &&
    !error &&
    !isFetching &&
    !hasNextPage &&
    allResults.length <= SIMILAR_MAX_RESULTS &&
    (!!(filters?.fromDate && filters.toDate) ||
      (!!filters?.region && filters.region !== 'all') ||
      !!filters?.category ||
      !!filters?.labelId ||
      !!filters?.facility)
  const { ref: loadMoreRef, inView } = useInView({
    rootMargin: '400px 0px',
    skip: !canAutoLoad,
  })

  useSearchResultsTracking({
    data,
    results: allResults,
    filters,
    ready: !isFetching,
    view: 'list',
  })

  useEffect(() => {
    if (inView && canAutoLoad && !isFetchingNextPage) {
      // fetchNextPage resolves (never rejects) also when the request fails.
      void fetchNextPage().then(() => {
        trackSearchLoadedMore(allResults.length)
      })
    }
  }, [inView, canAutoLoad, isFetchingNextPage, allResults.length, fetchNextPage])

  return (
    <>
      {error && <Error error="Nastala chyba u načítání dat" />}
      {!error && (
        <>
          <div className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
            {isFetching && !isFetchingNextPage ? (
              Array.from({ length: pageSize }).map((_, i) => <SearchResultSkeleton key={i} />)
            ) : allResults.length === 0 ? (
              <EmptyState onClearFilters={onClearFilters} filtersActive={filtersActive} />
            ) : (
              <>
                {allResults.map((accommodation, key) => (
                  <SearchResultCard searchResult={accommodation} key={key} position={key} />
                ))}
                {isFetchingNextPage &&
                  Array.from({ length: pageSize }).map((_, i) => <SearchResultSkeleton key={`loading-${i}`} />)}
              </>
            )}
          </div>
          {linkToSearch && (
            <div className="mt-8 flex justify-center">
              <Button asChild variant="ghost" className="text-red" size="lg">
                <Link to="/ubytovani/hledat" className="flex items-center justify-center gap-2">
                  Prozkoumat vše
                  <ArrowRightIcon className="mt-1 h-5 w-5" />
                </Link>
              </Button>
            </div>
          )}
          {canAutoLoad && <div ref={loadMoreRef} className="h-px" />}
          {similarVisible && <SearchSimilar resultsCount={allResults.length} />}
          {showClearFiltersCta && (
            <div className="mt-8 flex flex-col items-center justify-center rounded-2xl bg-slate-50 px-6 py-8 text-center">
              <h3 className="mb-2 text-lg font-semibold text-slate-700">Hledáte něco jiného?</h3>
              <p className="mb-4 max-w-md text-slate-500">Po zrušení filtrů uvidíte další nabídky.</p>
              <Button variant="secondary" size="sm" onClick={onClearFilters}>
                Vymazat filtry
              </Button>
            </div>
          )}
        </>
      )}
    </>
  )
}

export default Search
